/**
 * 月間ビューページ
 *
 * 1ヶ月分の習慣達成状況をカレンダー形式で表示する。
 * 習慣を選択して、その月の達成状況を視覚的に確認できる。
 *
 * 構成:
 * - 月ナビゲーション: 前月/次月への移動
 * - 習慣セレクター: 表示する習慣を選択するタブ
 * - カレンダーグリッド: 日付ごとの達成状況
 *
 * Rails で言えば:
 * - app/controllers/habits_controller.rb#monthly のビューに相当
 * - データは Inertia.js 経由で props として受け取る
 */

import { useState, useCallback, useMemo, useEffect } from 'react'
import { Head, router, usePage } from '@inertiajs/react'
import { ChevronLeft, ChevronRight, Plus } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { HabitLayout, HabitForm, HabitLogModal } from '@/components/habits'
import { SatisfactionEmoji } from '@/components/satisfaction'
import { useHabitToggle, useHabitSchedule, useAchievementRate } from '@/hooks'
import { cn } from '@/lib/utils'
import { formatDateLocal } from '@/lib/date'
import type { HabitsMonthlyProps } from '@/types'
import { WEEKDAY_LABELS_MONDAY_START } from '@/types'
import * as Routes from '@/lib/routes'

// === ヘルパー関数 ===

/**
 * 月のカレンダー日付配列を生成（月曜始まり）
 *
 * 月初の曜日に合わせて null でパディングし、
 * その月の全日付を Date オブジェクトとして返す。
 *
 * @param month - "YYYY-MM" 形式の月文字列
 * @returns カレンダーグリッド用の日付配列（null はパディング）
 */
function getCalendarDays(month: string): (Date | null)[] {
  const [year, monthNum] = month.split('-').map(Number)
  const firstDay = new Date(year, monthNum - 1, 1)
  const lastDay = new Date(year, monthNum, 0)

  const days: (Date | null)[] = []

  // 月初の曜日に合わせてパディング（月曜始まり）
  // getDay(): 日曜=0, 月曜=1, ... 土曜=6
  // 月曜始まりのインデックス: 月曜=0, 火曜=1, ... 日曜=6
  const mondayBasedDay = (firstDay.getDay() + 6) % 7
  for (let i = 0; i < mondayBasedDay; i++) {
    days.push(null)
  }

  // 月の全日付を追加
  for (let d = 1; d <= lastDay.getDate(); d++) {
    days.push(new Date(year, monthNum - 1, d))
  }

  return days
}

/**
 * 月ラベルを生成（例: "2024年1月"）
 */
function formatMonthLabel(month: string): string {
  const [year, monthNum] = month.split('-').map(Number)
  return `${year}年${monthNum}月`
}

/**
 * URL クエリパラメータから習慣IDを取得
 */
function getHabitIdFromUrl(url: string): number | null {
  const params = new URLSearchParams(url.split('?')[1] || '')
  const habitParam = params.get('habit')
  return habitParam ? parseInt(habitParam, 10) : null
}

/**
 * 初期選択する習慣IDを決定
 * URL パラメータがあればそれを使用、なければ最初の習慣
 */
function getInitialHabitId(
  habits: { id: number }[],
  urlHabitId: number | null
): number | null {
  if (urlHabitId !== null && habits.some((h) => h.id === urlHabitId)) {
    return urlHabitId
  }
  return habits[0]?.id ?? null
}

export default function HabitsMonthly({
  habits: initialHabits,
  month,
  startDate: _startDate,
  endDate: _endDate,
  today, // サーバーから提供される「今日」を使用
  satisfactions = {},
}: HabitsMonthlyProps) {
  // 未使用の props を明示的に無視
  void _startDate
  void _endDate

  // === URL 状態の取得 ===
  const { url } = usePage()
  const urlHabitId = getHabitIdFromUrl(url)

  // === 状態管理 ===
  const [habits, setHabits] = useState(initialHabits)
  const [selectedHabitId, setSelectedHabitIdState] = useState<number | null>(
    () => getInitialHabitId(initialHabits, urlHabitId)
  )
  const [showForm, setShowForm] = useState(false)

  // === カスタムフック ===
  // 習慣の完了/予定判定フック
  const { isCompleted, isScheduled } = useHabitSchedule()

  // カレンダー日付を計算
  const calendarDays = useMemo(() => getCalendarDays(month), [month])

  // 有効な日付のみ抽出（null を除外）
  const validDays = useMemo(
    () => calendarDays.filter((day): day is Date => day !== null),
    [calendarDays]
  )

  // 達成率計算フック
  const { calculateRate } = useAchievementRate(validDays)

  // トグル処理フック
  const {
    toggle,
    isPending,
    logModalTarget,
    closeLogModal,
    saveLog,
    uncomplete,
  } = useHabitToggle({
    onError: (error) => console.error('Toggle failed:', error),
  })

  // 選択中の習慣
  const selectedHabit = habits.find((h) => h.id === selectedHabitId)

  // === 習慣選択の状態管理 ===
  /**
   * 習慣を選択し、URL パラメータも更新
   * ページリロードなしで URL を更新することでブックマーク対応
   */
  const setSelectedHabitId = useCallback((habitId: number | null) => {
    setSelectedHabitIdState(habitId)
    if (habitId !== null) {
      const params = new URLSearchParams(window.location.search)
      params.set('habit', String(habitId))
      const newUrl = `${window.location.pathname}?${params.toString()}`
      window.history.replaceState({}, '', newUrl)
    }
  }, [])

  // 月移動時に状態を同期
  useEffect(() => {
    setHabits(initialHabits)
    // 選択中の習慣を維持、なければ URL パラメータまたは最初の習慣
    const currentUrlHabitId = getHabitIdFromUrl(url)
    const newSelectedId = getInitialHabitId(
      initialHabits,
      currentUrlHabitId ?? selectedHabitId
    )
    setSelectedHabitIdState(newSelectedId)
  }, [initialHabits, url])

  // === イベントハンドラー ===
  /**
   * 前月/次月に移動
   */
  const navigateMonth = (direction: 'prev' | 'next') => {
    const [year, monthNum] = month.split('-').map(Number)
    const newDate = new Date(
      year,
      monthNum - 1 + (direction === 'next' ? 1 : -1),
      1
    )
    const newMonth = `${newDate.getFullYear()}-${String(newDate.getMonth() + 1).padStart(2, '0')}`
    const params: { month: string; habit?: string } = { month: newMonth }
    if (selectedHabitId !== null) {
      params.habit = String(selectedHabitId)
    }
    router.get(Routes.monthly_habits_path(), params, { preserveState: true })
  }

  /**
   * 習慣の完了/未完了を切り替える
   */
  const handleToggle = useCallback(
    (date: Date) => {
      if (!selectedHabit) return

      const dateStr = formatDateLocal(date)
      const completed = isCompleted(selectedHabit, date)

      toggle(
        selectedHabit.id,
        dateStr,
        completed,
        (newCompleted) => {
          setHabits((prev) =>
            prev.map((h) => {
              if (h.id !== selectedHabit.id) return h

              // 完了日リストを更新
              const newDates = newCompleted
                ? [...(h.completedDates || []), dateStr]
                : (h.completedDates || []).filter((d) => d !== dateStr)

              // 達成率を再計算
              const newRate = calculateRate(h, newDates)

              return {
                ...h,
                completedDates: newDates,
                achievementRate: newRate,
              }
            })
          )
        },
        {
          enabled: !!selectedHabit.loggingEnabled,
          habitName: selectedHabit.name,
          existingLog: selectedHabit.logDetails?.[dateStr],
        }
      )
    },
    [selectedHabit, isCompleted, toggle, calculateRate]
  )

  // === レンダリング ===
  return (
    <>
      <Head title="月間ビュー | Habit Tracker" />

      <HabitLayout
        title="月間ビュー"
        action={
          <Button onClick={() => setShowForm(true)} className="touch-target">
            <Plus className="mr-1 h-5 w-5" />
            <span className="hidden sm:inline">新しい習慣</span>
          </Button>
        }
      >
        {/* === 月ナビゲーション === */}
        <div className="mb-6 flex items-center justify-between">
          <Button
            variant="ghost"
            size="icon"
            onClick={() => navigateMonth('prev')}
            className="touch-target"
          >
            <ChevronLeft className="h-5 w-5" />
          </Button>
          <h2 className="text-lg font-semibold">{formatMonthLabel(month)}</h2>
          <Button
            variant="ghost"
            size="icon"
            onClick={() => navigateMonth('next')}
            className="touch-target"
          >
            <ChevronRight className="h-5 w-5" />
          </Button>
        </div>

        {/* === 習慣セレクター === */}
        {habits.length > 0 && (
          <>
            {/* モバイル: ドロップダウン */}
            <div className="mb-4 sm:hidden">
              <select
                value={selectedHabitId ?? ''}
                onChange={(e) => setSelectedHabitId(Number(e.target.value))}
                className="bg-card border-border text-foreground w-full rounded-lg border px-4 py-3 text-sm font-medium"
              >
                {habits.map((habit) => (
                  <option key={habit.id} value={habit.id}>
                    {habit.name} ({habit.achievementRate ?? 0}%
                    {(habit.achievementRate ?? 0) > 100 ? '★' : ''})
                  </option>
                ))}
              </select>
            </div>

            {/* デスクトップ: 横スクロールタブ */}
            <div className="-mx-4 mb-4 hidden gap-2 overflow-x-auto px-4 pb-4 sm:flex">
              {habits.map((habit) => (
                <button
                  key={habit.id}
                  onClick={() => setSelectedHabitId(habit.id)}
                  className={cn(
                    'touch-target rounded-full px-4 py-2 text-sm font-medium whitespace-nowrap transition-colors',
                    selectedHabitId !== habit.id &&
                      'bg-muted text-muted-foreground hover:bg-muted/80'
                  )}
                  style={
                    selectedHabitId === habit.id
                      ? {
                          backgroundColor: habit.color || '#3b82f6',
                          color: 'white',
                        }
                      : undefined
                  }
                >
                  {habit.name}
                  <span className="ml-1 opacity-80">
                    {habit.achievementRate ?? 0}%
                    {(habit.achievementRate ?? 0) > 100 && '★'}
                  </span>
                </button>
              ))}
            </div>
          </>
        )}

        {/* === カレンダーグリッド === */}
        {selectedHabit && (
          <div className="bg-card border-border rounded-xl border p-4 sm:mx-auto sm:max-w-md">
            {/* 曜日ヘッダー（月曜始まり） */}
            <div className="mb-2 grid grid-cols-7 gap-1">
              {WEEKDAY_LABELS_MONDAY_START.map((label) => (
                <div
                  key={label}
                  className="text-muted-foreground py-2 text-center text-xs font-medium"
                >
                  {label}
                </div>
              ))}
            </div>

            {/* カレンダー日付 */}
            <div className="grid grid-cols-7 gap-1">
              {calendarDays.map((day, index) => {
                // パディング用の空セル
                if (!day) {
                  return (
                    <div key={`empty-${index}`} className="aspect-square" />
                  )
                }

                const dateStr = formatDateLocal(day)
                const scheduled = isScheduled(selectedHabit, day)
                const completed = isCompleted(selectedHabit, day)
                const extra = !scheduled // 予定外日
                const isToday = dateStr === today
                const habitColor = selectedHabit.color || '#3b82f6'

                return (
                  <button
                    key={dateStr}
                    onClick={() => handleToggle(day)}
                    disabled={isPending(selectedHabit.id, dateStr)}
                    className={cn(
                      'flex aspect-square flex-col items-center justify-center rounded-lg transition-all',
                      // 予定日の未完了
                      scheduled && !completed && 'bg-muted hover:bg-muted/80',
                      // 予定外（extra）の未完了
                      extra && !completed && 'hover:bg-muted/50',
                      isPending(selectedHabit.id, dateStr) && 'animate-pulse'
                    )}
                    style={{
                      // 完了した日は習慣カラーで塗りつぶし
                      ...(completed
                        ? {
                            backgroundColor: extra
                              ? `${habitColor}b3`
                              : habitColor, // extra: 70% opacity
                            color: 'white',
                            borderStyle: extra ? 'dashed' : 'solid',
                            borderWidth: extra ? '2px' : '0',
                            borderColor: extra ? habitColor : 'transparent',
                          }
                        : {}),
                      // 予定外の未完了
                      ...(extra && !completed
                        ? {
                            borderStyle: 'dashed',
                            borderWidth: '1px',
                            borderColor: '#9ca3af', // gray-400
                          }
                        : {}),
                      // 今日はリング装飾
                      ...(isToday
                        ? {
                            boxShadow: `0 0 0 2px ${habitColor}`,
                            outline: '2px solid transparent',
                            outlineOffset: '2px',
                          }
                        : {}),
                    }}
                  >
                    <span className="text-sm font-medium">{day.getDate()}</span>
                    <SatisfactionEmoji
                      level={satisfactions[dateStr] ?? null}
                      size="xs"
                    />
                  </button>
                )
              })}
            </div>
          </div>
        )}

        {/* 習慣がない場合の空状態 */}
        {habits.length === 0 && (
          <div className="py-12 text-center">
            <p className="text-muted-foreground mb-4">習慣がまだありません</p>
            <Button onClick={() => setShowForm(true)}>
              <Plus className="mr-1 h-5 w-5" />
              習慣を追加
            </Button>
          </div>
        )}
      </HabitLayout>

      {/* Form Modal */}
      {showForm && <HabitForm onClose={() => setShowForm(false)} />}

      {/* Log Modal */}
      {logModalTarget && (
        <HabitLogModal
          habitName={logModalTarget.habitName}
          initialData={logModalTarget.existingLog}
          mode={logModalTarget.mode}
          onSave={(data) =>
            saveLog(logModalTarget.habitId, logModalTarget.date, data)
          }
          onSkip={closeLogModal}
          onUncomplete={
            logModalTarget.mode === 'edit'
              ? () => {
                  const { habitId, date } = logModalTarget
                  uncomplete(habitId, date, () => {
                    setHabits((prev) =>
                      prev.map((h) => {
                        if (h.id !== habitId) return h
                        const newDates = (h.completedDates || []).filter(
                          (d) => d !== date
                        )
                        const newRate = calculateRate(h, newDates)
                        return {
                          ...h,
                          completedDates: newDates,
                          achievementRate: newRate,
                        }
                      })
                    )
                  })
                }
              : undefined
          }
        />
      )}
    </>
  )
}
